import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
	type ChatCounterpart,
	DESK_COUNTERPART,
	type IOwnershipContext,
	type ISupportMessage,
} from '../../../interface/support-chat';
import { counterpartCopy } from '../../../lib/chat-counterpart-copy';
import { isOwnMessage } from '../../../lib/support-api';
import { lastReadOwnSequence } from '../../../lib/support-read-receipts';
import ChatMessageRow from './ChatMessageRow';

/** Day divider label. Relative for the two days an agent actually cares about. */
const dayLabel = (iso: string): string => {
	const date = new Date(iso);
	const today = new Date();
	const yesterday = new Date();
	yesterday.setDate(today.getDate() - 1);

	const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
	if (sameDay(date, today)) return 'Today';
	if (sameDay(date, yesterday)) return 'Yesterday';

	return date.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
};

/** The support empty state. A peer conversation passes its own. */
const DESK_EMPTY_STATE = {
	title: 'Ask the support desk anything',
	body: (
		<>Result sheet won&apos;t upload? Wrong polling unit? Someone blocking you? Type it here — the desk sees it straight away.</>
	),
};

interface IChatTranscript {
	messages: ISupportMessage[];
	/** Who is reading, and how to tell their messages from the other party's. */
	ownership: IOwnershipContext;
	counterpart?: ChatCounterpart;
	emptyState?: { title: string; body: React.ReactNode };
	onRetry?: (clientId: string) => void;
	localPreviews?: Record<string, string>;
	uploadPercents?: Record<string, number>;
	needsReattach?: Set<string>;
	onReattach?: (clientId: string, file: File) => void;
	/** Persisted thumbnails by clientId, for bubbles whose blob is gone. */
	thumbnails?: Record<string, string>;
	/** Long-press (or Enter) on a message row. */
	onOpenActions?: (messageId: string) => void;
	/** Tap-to-reply, used by result request cards. */
	onReplyTo?: (messageId: string) => void;
	/** Ref callback from useReadReceipts — attaches the dwell observer to a bubble. */
	observeRead?: (element: HTMLElement | null, sequence: number) => void;
	loading?: boolean;
	/** One muted line above the first message — what this conversation is about. */
	preamble?: string | null;
}

/**
 * The conversation, oldest at the top.
 *
 * <p>Deliberately the opposite order to <c>IncidentActivityStream</c>: that is a record you
 * reconstruct after the fact, this is a conversation you are inside.</p>
 *
 * <p>Not virtualized, and that is a decision rather than an omission. A conversation on
 * election day is tens of messages, not thousands; on a low-end Android `measureElement`
 * forces layout on every scroll frame and makes the list JANKIER, not smoother. A window plus
 * "load earlier" is both simpler and faster here.</p>
 */
const ChatTranscript = ({
	messages,
	ownership,
	counterpart = DESK_COUNTERPART,
	emptyState = DESK_EMPTY_STATE,
	onRetry,
	localPreviews,
	uploadPercents,
	needsReattach,
	onReattach,
	thumbnails,
	onOpenActions,
	onReplyTo,
	observeRead,
	loading,
	preamble,
}: IChatTranscript) => {
	const scrollRef = useRef<HTMLOListElement>(null);
	const pinnedRef = useRef(true);
	const previousCount = useRef(messages.length);
	// The announcer's OWN counter. It used to read `previousCount`, which the scroll layout
	// effect below had already advanced by the time this ran — so the delta was always 0 and
	// nothing was ever announced. Same defect the admin desk's transcript had.
	const announcedCount = useRef(messages.length);
	// The first non-empty render is the page loading, not messages arriving.
	const hasFilled = useRef(messages.length > 0);
	// Read through refs so the effect keys on the count alone — `ownership` is a fresh object
	// on every render of the parent.
	const messagesRef = useRef(messages);
	messagesRef.current = messages;
	const ownershipRef = useRef(ownership);
	ownershipRef.current = ownership;
	const announceToggle = useRef(false);
	const [announcement, setAnnouncement] = useState('');
	const [now, setNow] = useState(() => Date.now());

	// One timer for every countdown in the list, rather than one per failed row.
	useEffect(() => {
		if (!messages.some((m) => m.state === 'failed')) return;
		const id = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(id);
	}, [messages]);

	/** Within 80px of the bottom counts as "following the conversation". */
	const isPinned = () => {
		const el = scrollRef.current;
		if (!el) return true;
		return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
	};

	const handleScroll = () => {
		pinnedRef.current = isPinned();
	};

	// useLayoutEffect, not useEffect: in useEffect the browser has already painted at the old
	// offset and the user sees the list jump.
	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const grew = messages.length > previousCount.current;
		previousCount.current = messages.length;

		// Never yank an unpinned reader to the bottom — they are reading something.
		if (grew && pinnedRef.current) el.scrollTop = el.scrollHeight;
	}, [messages]);

	// Coalesced announcement. A screen reader narrating forty arrivals during a collation
	// surge makes the app unusable, so a burst becomes one count. Same shape as
	// IncidentActivityStream, which learned this the hard way.
	useEffect(() => {
		const current = messagesRef.current;
		const delta = current.length - announcedCount.current;
		announcedCount.current = current.length;
		if (delta <= 0) return;

		// The first fill is the transcript loading. Nothing "arrived".
		if (!hasFilled.current) {
			hasFilled.current = true;
			return;
		}

		// Only the OTHER side's arrivals are news; the reader just typed their own.
		const incoming = current.slice(-delta).filter((m) => !isOwnMessage(m, ownershipRef.current)).length;
		if (incoming === 0) return;

		// Two identical announcements in a row are one to React (same string, no re-render) and
		// therefore one to the screen reader. A zero-width space alternated on the end makes
		// each burst a distinct string without changing what is read out.
		announceToggle.current = !announceToggle.current;
		const text = incoming === 1 ? '1 new message' : `${incoming} new messages`;
		setAnnouncement(announceToggle.current ? `${text}\u200B` : text);
	}, [messages.length]);

	// Mounted in BOTH branches, so it exists before its first text: a live region created in the
	// same render as its content is not announced.
	const liveRegion = (
		<span
			className='visually-hidden'
			aria-live='polite'
		>
			{announcement}
		</span>
	);

	if (!loading && messages.length === 0) {
		return (
			<div className='support-chat__transcript'>
				{liveRegion}
				<div className='sc-empty'>
					<h3>{emptyState.title}</h3>
					<p>{emptyState.body}</p>
				</div>
			</div>
		);
	}

	// Rendered ONCE under the newest read message rather than as a glyph on every bubble. A wall
	// of blue ticks carries the same information and reads as noise.
	const readMark = lastReadOwnSequence(
		messages.map((m) => ({ sequence: m.sequence, own: isOwnMessage(m, ownership), readByOther: m.readByOther }))
	);

	let lastDay = '';

	return (
		<>
			{liveRegion}

			<ol
				className='support-chat__transcript'
				role='log'
				ref={scrollRef}
				onScroll={handleScroll}
				// aria-live is off: the coalesced region above does the announcing. A live log
				// would read every message on every change.
				aria-live='off'
				aria-label={counterpartCopy(counterpart).transcriptLabel}
			>
				{preamble ? <li className='sc-day sc-day--context'>{preamble}</li> : null}
				{messages.map((message) => {
					const day = dayLabel(message.createdAt);
					const showDay = day !== lastDay;
					lastDay = day;

					const own = isOwnMessage(message, ownership);

					return (
						<React.Fragment key={message.clientId ?? message.id}>
							{showDay ? <li className='sc-day'>{day}</li> : null}
							<li
								className='sc-item'
								ref={observeRead ? (el) => observeRead(el, message.sequence) : undefined}
							>
								<ChatMessageRow
									message={message}
									own={own}
									ownership={ownership}
									counterpart={counterpart}
									now={now}
									onRetry={onRetry}
									localPreviewUrl={message.clientId ? localPreviews?.[message.clientId] : undefined}
									uploadPercent={message.clientId ? uploadPercents?.[message.clientId] : undefined}
									needsReattach={!!message.clientId && !!needsReattach?.has(message.clientId)}
									onReattach={onReattach}
									thumbnailDataUrl={message.clientId ? thumbnails?.[message.clientId] : undefined}
									onOpenActions={onOpenActions}
									onReplyTo={onReplyTo}
								/>
							</li>
							{readMark !== null && message.sequence === readMark ? (
								<li className='sc-readline'>
									Read {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
								</li>
							) : null}
						</React.Fragment>
					);
				})}
			</ol>
		</>
	);
};

export default ChatTranscript;
